/**
 * Куда ведёт ссылка в уведомлении — и как она подписана.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ССЫЛКА ТЕПЕРЬ ПРИКРЕПЛЯЕТСЯ РУКАМИ, А НЕ ВЕШАЕТСЯ САМА.
 *
 * 25.08.2026 владелец написал: «В уведомлениях везде вешается ссылка
 * на ключ или тариф, нужно чтобы я мог сам её прикреплять или
 * не прикреплять, не везде уведомления будут с ссылкой».
 *
 * Так и было: у эталона подпись ссылки задана ВИДОМ уведомления
 * (`ntKindData`: «работы» → «Посмотреть тарифы», «важно» → «Обновить
 * ключ в кабинете»), и мы это списали. Хуже того — списали
 * ПОЛОВИНЧАТО: подпись рисовалась в предпросмотре, а на сервер
 * уходило `url: null`, и в колокольчике клиента ссылки не было вовсе.
 * То есть сотрудник видел ссылку, которой клиент не получал.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ СПИСОК, А НЕ ПОЛЕ ДЛЯ ЛЮБОГО АДРЕСА.
 *
 * Во-первых, у ссылки должна быть ПОДПИСЬ, и подпись эта — не адрес:
 * «Обновить ключ в кабинете» человек читает и понимает, `/rk/klyuch`
 * не говорит ему ничего. Хранить подпись отдельным полем негде —
 * у записи уведомления его нет, — а выводить из адреса значит гадать.
 *
 * Во-вторых, рассылка уходит сотням пространств разом и отменить её
 * нельзя. Опечатка в адресе, набранном руками, — это сотни человек,
 * пришедших на «страница не найдена», и вторая рассылка с извинением.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * 26.08.2026 СПИСОК ОТКРЫТ — И ОТКРЫТ ЧЕСТНО.
 *
 * Владелец: пусть адрес будет любой, а рядом — поле под подпись.
 * И это верно: список был закрыт не из осторожности, а по нужде —
 * подпись брать было неоткуда.
 *
 * Теперь у записи уведомления есть своё поле `linkText`, и обе части
 * ссылки задаются вместе: куда ведём и что написано на кнопке.
 * Пять готовых мест никуда не делись — они остались КНОПКАМИ БЫСТРОГО
 * ВЫБОРА: девять уведомлений из десяти ведут туда же, куда и вчера,
 * и заставлять набирать `/rk/cabinets` руками ради этого незачем.
 *
 * ОПАСЕНИЕ ОБ ОПЕЧАТКЕ ОСТАЛОСЬ, И ОТВЕТ НА НЕГО — ПРЕДПРОСМОТР.
 * Рассылка уходит сотням разом и не отменяется; адрес, набранный
 * руками, виден в предпросмотре ровно тем же, чем его увидит клиент,
 * и стоит там ссылкой, по которой можно нажать до отправки.
 * ═══════════════════════════════════════════════════════════════════════
 */
export interface МестоСсылки {
  /** Ключ в записи уведомления — он же адрес, куда ведём. */
  readonly url: string;
  /** Что написано на ссылке. */
  readonly подпись: string;
  /** Как назвать место в выборе сотрудника. */
  readonly выбор: string;
}

export const МЕСТА_ССЫЛКИ: readonly МестоСсылки[] = [
  { url: '/tarify', подпись: 'Посмотреть тарифы', выбор: 'Тарифы на сайте' },
  { url: '/rk/cabinets', подпись: 'Обновить ключ в кабинете', выбор: 'Ключи кабинетов' },
  { url: '/rk/plan', подпись: 'Открыть подписку', выбор: 'Подписка и счета' },
  { url: '/rk', подпись: 'Открыть кабинет', выбор: 'Кабинет' },
  { url: '/blog', подпись: 'Читать в блоге', выбор: 'Блог' },
];

/**
 * Что предложить по виду уведомления.
 *
 * ПРЕДЛОЖИТЬ, А НЕ НАВЯЗАТЬ: это подстановка в поле, которое сотрудник
 * тут же меняет или снимает. Слова — эталонные (`ntKindData`).
 */
export const ССЫЛКА_ПО_ВИДУ: Readonly<Record<string, string | null>> = {
  works: '/tarify',
  important: '/rk/cabinets',
  sale: '/tarify',
  service: null,
};

/**
 * Подпись ссылки.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СВОЯ ПОДПИСЬ ПОБЕЖДАЕТ СПИСОК, А НЕ НАОБОРОТ.
 *
 * Сотрудник, написавший подпись руками, хотел именно её. Возьми мы
 * при этом слова из списка — и `/tarify` со своей подписью «Успеть
 * до пятницы» ушёл бы клиенту как «Посмотреть тарифы», то есть
 * не тем, что видел отправитель.
 *
 * НЕТ СВОЕЙ — СПИСОК, а незнакомый адрес — «Открыть». Так подписаны
 * все уведомления, разосланные до появления поля: пустота в нём
 * означает «как раньше», а не «без подписи».
 * ═══════════════════════════════════════════════════════════════════════
 */
export function подписьСсылки(url: string | null, своя?: string | null): string | null {
  if (url === null || url.trim() === '') return null;
  const набрана = (своя ?? '').trim();
  if (набрана !== '') return набрана;
  return МЕСТА_ССЫЛКИ.find((м) => м.url === url)?.подпись ?? 'Открыть';
}
